import {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Divider, Typography,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton,
    Skeleton, CircularProgress
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VehicleLoanDetail from '@/pages/components/audit/adjustment/adjust/dialogs/loan/sub/VehicleLoanDetail';
import {
    FsCategoryLine,
    useAdjustmentStore,
    useAdjustDraftStore,
    useFsAdjustStore,
    useCompanyAnnualStore,
    useFsCategoryStore
} from '@/store/audit';
import {useAdjustDialogData} from '@/adjustment/hooks/useAdjustDialogData';
import {useWpGlInvoiceDetail} from '@/adjustment/hooks/useWpGlInvoiceDetail';
import WpGlInvoiceDetailDialog, {WpGlInvoiceDetailButton} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/WpGlInvoiceDetailDialog';
import {WpHeader, WpPerCheck, WpInterestPerCheck, WpCheckToolbar} from '@/adjustment/adjust/dialogs/basic/common';
import {useWpIssueMark} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/useWpIssueMark';
import {buildIssueMarkPayload} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpStatusSaveHelper';
import {invalidateWpSpecialSyncQueries} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/invalidateWpSpecialSyncQueries';
import {useQueryClient} from '@tanstack/react-query';
import {getWorkingPaper, saveOrUpdate} from '@/services/auditService/auditWorkingPaperService';
import {formatToday, convertToDisplayFormat, convertToISOFormat, toNumber} from '@/utils/dataUtils';
import {SnackbarField} from '@/components/common/SnackbarField';
import {useWpDialogSnackbar} from '@/adjustment/hooks/WpSnackbarContext';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {MoneyField} from '@/components/common/MoneyField';
import {useWpIdleAutoSave} from '@/hooks/useWpIdleAutoSave';
import {dialogContentElRef} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/dialogContentElRef';
import {AutoSaveStatus} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/AutoSaveStatus';
import {
    resolveWpPersistOptions,
    type WpPersistOptions,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpPersistOptions';
import {
    calculateIsUnbalanced,
} from '@/adjustment/adjust/support';
import {IntangibleRow, PPERow, VehicleLoanRow} from "@/services/auditService/interface/wp-row";
import {VehicleLoanWorkingPaper} from '@/services/auditService/interface/loan';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import RepaymentScheduleDrawer, {type RepaymentScheduleDrawerDataLoaded} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import type {LoanScheduleRequest} from '@/services/auditService/auditLoanCalculatorService';
import LoanFieldAdjustButton from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanFieldAdjustButton';
import LoanFieldAdjustPanel, {
    type LoanFieldAdjustTarget,
    type LoanFieldAdjustActionState,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanFieldAdjustPanel';
import LoanWpSkeleton from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanWpSkeleton';
import {getLoanRowFieldAdj, sumLoanFieldAdjList} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanFieldAdjUtils';
import LoanAdjAmountBadge from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanAdjAmountBadge';
import {AdjustmentDetailsDrawer} from '@/adjustment/adjust/dialogs';
import {useAdjustmentDetailsDrawer} from '@/adjustment/hooks/useAdjustmentDetailsDrawer';
import EpWorkingContent from '@/adjustment/adjust/support/EpWorkingContent';
import {useSeamlessWpChrome} from '@/adjustment/hooks/useSeamlessWpChrome';
import {WpSeamlessPeriodTabsSlot} from '@/adjustment/hooks/WpPeriodTabsUi';
import {WpNotesAnchor} from '@/adjustment/adjust/support/WpNotesContext';
import EmptyConfirmDialog from '@/adjustment/adjust/support/EmptyConfirmDialog';
import {isPpeRowsBlank} from '@/adjustment/hooks/ppeGlPrefill';
import {isItlSectionEmpty} from '@/adjustment/adjust/dialogs/asset/itlWpHelpers';
import {
    aggregateVehicleLoanTotals,
    buildVehicleLoanSumsForSave,
    mergeVehicleLoansForTotals,
    resolveVehicleLoanDisplayValue,
    vehicleLoanSumsHasTrackableValues,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/vehicleLoanSumUtils';

export type VehicleLoanProps = {
    open: boolean;
    onClose: () => void;
    payload?: {
        title?: string;
        annualId?: number;
        registryKey?: string;
        statement?: 'BS' | 'PL';
        companyId?: any;
        line?: FsCategoryLine;
        parDetail?: any;
        setParDetail?: (data: any) => void;
        workingRecordData?: any[] | null;
        workingRecordLoading?: boolean;
        onRequestOpenCode?: (code: string) => void;
    } & Record<string, any>;
    embed?: boolean;
    onSnackbar?: ShowSnackbarFn;
};

interface PPEData {
    ppeId?: number;
    mv: string;
    purchaseCost: number;
}

interface VehicleLoanValidationState {
    hasData: boolean;
    missingFields: string[];
}

export default function VehicleLoan({open, onClose, payload, embed, onSnackbar}: VehicleLoanProps) {
    const companyName = useFsCategoryStore((s) => s.companyName);
    const headerAnnualId = useAdjustmentStore((s) => Number(s.header?.annualId || 0) || 0);
    const derivedAnnualId = useMemo(
        () => Number(payload?.annualId || 0) || headerAnnualId || 0,
        [payload?.annualId, headerAnnualId]
    );

    // Get firstAudit from companyAnnualStore
    const firstAudit = useCompanyAnnualStore((s) => s.byAnnualId[derivedAnnualId]?.firstAudit);

    const scheduleName = useMemo(() => {
        return payload?.line?.scheduleName || '';
    }, [payload?.line?.scheduleName]);

    const adjustDraftStore = useAdjustDraftStore();
    const isControlled = useMemo(() => payload?.setParDetail !== undefined, [payload]);
    const isBreakdownMode = useMemo(() => payload?.mode === AdjustmentMode.BREAKDOWM, [payload?.mode]);

    // 无论通过 BS-NCL-OFL 还是 BS-CL-OFL 访问，后端请求统一使用 BS-CL-OFL
    const backendCode = useMemo(() => {
        const raw = payload?.code || payload?.registryKey || payload?.line?.code || '';
        if (raw === 'BS-NCL-OFL') return 'BS-CL-OFL';
        return raw;
    }, [payload?.code, payload?.registryKey, payload?.line?.code]);

    // 编辑模式判断：移除模式限制，始终允许编辑
    const isEditableMode = true;

    // Validation errors state
    type LoanValidationErrors = {
        [index: number]: {
            [field: string]: string;
        };
    };
    const [validationErrors, setValidationErrors] = useState<LoanValidationErrors>({});

    const {
        meta,
        currentData,
        updateData,
        initialized,
    } = useAdjustDialogData({
        open,
        annualId: derivedAnnualId,
        code: backendCode,
        companyId: Number(payload?.companyId),
        isControlled,
        parDetail: payload?.parDetail,
        setParDetail: payload?.setParDetail,
        line: payload?.line,
        initialAdjustment: payload?.initialAdjustment,
        isToPaper: false,
        preferBackendData: !isControlled,
        workflow: payload?.workflow,
        initialDataFactory: useCallback((line: FsCategoryLine, initialAdjustment?: number) => ({
            bankLoans: [],
            vehicleLoans: []
        }), []),
    });

    const {yoa, lastYoa, currency, categoryLine, periodTo, periodFrom} = meta;

    const {
        isMultiPeriodTabs,
        headerPeriodFrom,
        headerPeriodTo,
        epWorkingMeta,
    } = useSeamlessWpChrome({
        open,
        payload,
        apiCode: backendCode,
        currentData,
        metaPeriodFrom: periodFrom,
        metaPeriodTo: periodTo,
    });

    /** MULTI: WpPerCheck / Interest YOA labels pinned to engagement span (not active Tab). */
    const stablePerCheckYoa = useMemo(() => {
        if (!isMultiPeriodTabs) {
            return {yoa: yoa ?? '', lastYoa: lastYoa ?? ''};
        }
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        if (periods.length < 2) {
            return {yoa: yoa ?? '', lastYoa: lastYoa ?? ''};
        }
        const first = periods[0];
        const last = periods[periods.length - 1];
        const yoaLabel = String(last?.yearOfAssessment || yoa || '').trim();
        const firstKey = String(first?.key || '').trim();
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        const onFirstTab = !!firstKey && !!activeKey && activeKey === firstKey;
        const rawFirstYoa = String(first?.yearOfAssessment || '').trim();
        const mm = /^(\d{4})\s*\/\s*(\d{2})$/.exec(rawFirstYoa);
        const derivedLastYoa = mm
            ? `${Number(mm[1]) - 1}/${String((Number(mm[2]) - 1 + 100) % 100).padStart(2, '0')}`
            : '';
        // First Tab meta.lastYoa is engagement last year; other Tabs are tab-shifted — never use those.
        const lastYoaLabel = (onFirstTab && lastYoa)
            || derivedLastYoa
            || (onFirstTab ? (lastYoa ?? '') : '')
            || '';
        return {yoa: yoaLabel, lastYoa: lastYoaLabel};
    }, [
        isMultiPeriodTabs,
        payload?.periods,
        payload?.periodScope?.sliceKey,
        yoa,
        lastYoa,
    ]);


    const adjDrawer = useAdjustmentDetailsDrawer();
    const [fieldAdjustTarget, setFieldAdjustTarget] = useState<LoanFieldAdjustTarget | null>(null);
    const [fieldAdjustAction, setFieldAdjustAction] = useState<LoanFieldAdjustActionState | null>(null);

    const handleFieldAdjustActivate = useCallback((target: LoanFieldAdjustTarget) => {
        setFieldAdjustTarget(target);
    }, []);

    const handleFieldAdjustClose = useCallback(() => {
        setFieldAdjustTarget(null);
        setFieldAdjustAction(null);
    }, []);

    const handleFieldAdjustActionStateChange = useCallback((state: LoanFieldAdjustActionState | null) => {
        setFieldAdjustAction(state);
    }, []);

    const showFieldAdjustmentButton = !!fieldAdjustAction?.shouldShowAdjustmentButton;

    const {issueMarkState, setIssueMarkState, validateIssueMarkBeforeSave} = useWpIssueMark({
        companyId: Number(payload?.companyId),
        annualId: derivedAnnualId,
        line: payload?.line,
        categoryLine,
    });

    const glDetail = useWpGlInvoiceDetail(currentData);
    const glCode = payload?.code || payload?.registryKey || payload?.line?.code || '';

    // WpPerCheck balance states
    const [currentPerOk, setCurrentPerOk] = useState<boolean>(true);
    const [nonCurrentPerOk, setNonCurrentPerOk] = useState<boolean>(true);
    const [interestPerOk, setInterestPerOk] = useState<boolean>(true);
    const queryClient = useQueryClient();
    const {showSnackbar, notifySaveResult, snackbarFieldProps} = useWpDialogSnackbar(onSnackbar, payload?.onSnackbar);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ppeDataList, setPpeDataList] = useState<PPEData[]>([]);
    const [vehicleLoansData, setVehicleLoansData] = useState<Partial<VehicleLoanRow>[]>([]);
    const [loanValidationStates, setLoanValidationStates] = useState<VehicleLoanValidationState[]>([]);

    // Ref to track if data has been fetched to prevent duplicate fetches
    const dataFetchedRef = useRef(false);

    // PPE 数据为空时的确认对话框状态
    const [emptyConfirmOpen, setEmptyConfirmOpen] = useState(false);
    const emptyConfirmHandledRef = useRef(false);

    // 记录已删除的 vehicle loan 索引，保存时排除这些记录，后端通过 ID 比对做逻辑删除
    const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set());

    // RepaymentScheduleDrawer states
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [currentScheduleRequest, setCurrentScheduleRequest] = useState<LoanScheduleRequest | null>(null);
    const [currentLoanIndex, setCurrentLoanIndex] = useState<number>(-1);

    const today = useMemo(() => formatToday(), []);

    // Get NON_VEHICLE_LOAN line data for fsId = 45
    const nonVehicleLoanLine = useFsAdjustStore(state => state.getLineByCode('NON_VEHICLE_LOAN'));
    const nonVehicleLoanFinalAmount = useMemo(() => {
        return nonVehicleLoanLine?.finalAmount || 0;
    }, [nonVehicleLoanLine]);

    // FS nodes for field-adjust Client (Current / Non-current / Interest)
    const currentOflLine = useFsAdjustStore(state => state.byCode['BS-CL-OFL'] ?? null);
    const nonCurrentOflLine = useFsAdjustStore(state => state.byCode['BS-NCL-OFL'] ?? null);
    const loanInterestLine = useFsAdjustStore(state => state.byCode['PL-FC-LI'] ?? null);

    /** FS node Client for LoanFieldAdjustPanel (same total for every loan column). */
    const fieldAdjustPerClientByKey = useMemo(() => {
        const clientOf = (line: {openingAmount?: unknown; amount?: unknown} | null) =>
            Number(line?.openingAmount ?? line?.amount ?? 0) || 0;
        return {
            current: clientOf(currentOflLine),
            nonCurrent: clientOf(nonCurrentOflLine),
            interest: clientOf(loanInterestLine),
        } as Record<string, number>;
    }, [currentOflLine, nonCurrentOflLine, loanInterestLine]);

    // VehicleLoan required fields for validation
    const vehicleLoanRequiredFields = useMemo(() => ['purchaseCost', 'downPayment', 'terms', 'totalInstalment', 'firstRepayment'], []);

    // Validate vehicle loan data
    const validateLoanData = useCallback((data: Partial<VehicleLoanRow>): VehicleLoanValidationState => {
        const missingFields: string[] = [];

        if (!data.downPayment || data.downPayment === 0) {
            missingFields.push('Down payment');
        }
        if (!data.terms || data.terms === 0) {
            missingFields.push('Terms');
        }
        if (!data.totalInstalment || data.totalInstalment === 0) {
            missingFields.push('Total Instalment');
        }
        if (!data.firstRepayment || data.firstRepayment === '') {
            missingFields.push('First repayment date');
        }

        return {
            hasData: missingFields.length === 0,
            missingFields
        };
    }, []);

    // Validate all vehicle loans - returns { valid, errors, filteredLoans }
    const validateVehicleLoans = useCallback((loansToValidate: Partial<VehicleLoanRow>[]): {
        valid: boolean;
        errors: LoanValidationErrors;
        filteredLoans: VehicleLoanRow[]
    } => {
        const errors: LoanValidationErrors = {};
        const filteredLoans: VehicleLoanRow[] = [];
        let hasErrors = false;

        loansToValidate.forEach((loan, index) => {
            const filledFields: string[] = [];
            const emptyFields: string[] = [];

            // Check each required field
            vehicleLoanRequiredFields.forEach(field => {
                const value = (loan as any)[field];
                if (value !== undefined && value !== null && value !== '' && value !== 0) {
                    filledFields.push(field);
                } else {
                    emptyFields.push(field);
                }
            });

            // If all fields are empty, skip this loan (will be filtered out)
            if (filledFields.length === 0) {
                return;
            }

            // If some fields have value but others don't, it's an error
            if (emptyFields.length > 0 && filledFields.length > 0) {
                errors[index] = {};
                emptyFields.forEach(field => {
                    errors[index][field] = `${field} is required`;
                });
                hasErrors = true;
            }

            // Add valid loans to filtered list
            if (Object.keys(loan).length > 0) {
                filteredLoans.push(loan as VehicleLoanRow);
            }
        });

        return {valid: !hasErrors, errors, filteredLoans};
    }, [vehicleLoanRequiredFields]);

    // 对话框关闭时重置状态
    useEffect(() => {
        if (!open) {
            dataFetchedRef.current = false;
            emptyConfirmHandledRef.current = false;
            setEmptyConfirmOpen(false);
            // Reset data to prevent stale state on reopen
            setPpeDataList([]);
            setVehicleLoansData([]);
            setLoanValidationStates([]);
            setDeletedIndices(new Set());
            setFieldAdjustTarget(null);
            setFieldAdjustAction(null);
        }
    }, [open]);

    // Fetch PPE data on component mount - optimized to call only once
    useEffect(() => {
        if (!open || !derivedAnnualId || !payload?.companyId) return;
        if (dataFetchedRef.current) return; // Prevent duplicate fetches
        dataFetchedRef.current = true;

        // 用于防止组件卸载后的异步更新
        let isMounted = true;

        const fetchPPEData = async () => {
            setLoading(true);
            try {
                // Hire-purchase sources: PPE motor (BS-NCA-PPE) + ITL taxi (BS-NCA-ITL)
                const [ppeResponse, itlResponse] = await Promise.all([
                    getWorkingPaper({
                        companyId: Number(payload.companyId),
                        annualId: derivedAnnualId,
                        code: 'BS-NCA-PPE',
                    }),
                    getWorkingPaper({
                        companyId: Number(payload.companyId),
                        annualId: derivedAnnualId,
                        code: 'BS-NCA-ITL',
                    }),
                ]);

                // 检查组件是否仍然挂载
                if (!isMounted) return;

                const ppeDetail = Array.isArray(ppeResponse) && ppeResponse.length > 0
                    ? ppeResponse[0]
                    : null;
                const itlDetail = Array.isArray(itlResponse) && itlResponse.length > 0
                    ? itlResponse[0]
                    : null;

                const hirePurchaseMotorRow: PPERow[] = Array.isArray(ppeDetail?.hirePurchaseMotorRow)
                    ? ppeDetail.hirePurchaseMotorRow
                    : [];
                const hirePurchaseTaxiCabRow: IntangibleRow[] = Array.isArray(itlDetail?.hirePurchaseTaxiCabRow)
                    ? itlDetail.hirePurchaseTaxiCabRow
                    : [];

                // Both hire-purchase sections empty (ignore blank seed rows) → guide user to PPE or ITL
                if (isPpeRowsBlank(hirePurchaseMotorRow) && isItlSectionEmpty(hirePurchaseTaxiCabRow)) {
                    if (isMounted && !emptyConfirmHandledRef.current) {
                        emptyConfirmHandledRef.current = true;
                        setEmptyConfirmOpen(true);
                    }
                    return;
                }

                const mapMotorToPpeData = (row: PPERow): PPEData | null => {
                    if (isPpeRowsBlank([row])) return null;
                    const ppeId = row.id;
                    return {
                        ...(ppeId != null && Number(ppeId) > 0 ? {ppeId} : {}),
                        mv: row.item || '',
                        purchaseCost: Number(row.additions || row.costBf || 0) || 0,
                    };
                };
                const mapTaxiToPpeData = (row: IntangibleRow): PPEData | null => {
                    if (isItlSectionEmpty([row])) return null;
                    const ppeId = row.id;
                    return {
                        ...(ppeId != null && Number(ppeId) > 0 ? {ppeId} : {}),
                        mv: row.item || '',
                        purchaseCost: Number(row.addition || row.costBf || 0) || 0,
                    };
                };

                const mappedData: PPEData[] = [
                    ...hirePurchaseMotorRow.map(mapMotorToPpeData),
                    ...hirePurchaseTaxiCabRow.map(mapTaxiToPpeData),
                ].filter((row): row is PPEData => row != null);

                if (!isMounted) return;
                setPpeDataList(mappedData);

                // Fetch existing vehicle loan data using getWorkingPaper API with code=BS-CL-OFL
                try {
                    const vehicleLoanResponse = await getWorkingPaper({
                        companyId: Number(payload.companyId),
                        annualId: derivedAnnualId,
                        code: 'BS-CL-OFL'
                    });

                    if (!isMounted) return;

                    // Extract vehicle loans from response
                    let existingVehicleLoans: VehicleLoanRow[] = [];

                    if (vehicleLoanResponse && Array.isArray(vehicleLoanResponse) && vehicleLoanResponse.length > 0) {
                        const detail = vehicleLoanResponse[0];
                        existingVehicleLoans = detail?.vehicleLoans || [];
                    }

                    if (existingVehicleLoans && existingVehicleLoans.length > 0) {
                        // Map existing data to match PPE data by ppeId first (fallback to MV)
                        // MV can be duplicated across multiple PPE rows; using MV as key will cause id reuse.
                        const vehicleLoansMap = new Map<string, VehicleLoanRow>();
                        existingVehicleLoans.forEach((loan: VehicleLoanRow) => {
                            if (loan.ppeId != null) {
                                vehicleLoansMap.set(`ppe:${loan.ppeId}`, loan);
                                return;
                            }
                            vehicleLoansMap.set(`mv:${loan.mv}`, loan);
                        });

                        const initializedLoans = mappedData.map(ppeData => {
                            const existingLoan =
                                ppeData.ppeId != null
                                    ? vehicleLoansMap.get(`ppe:${ppeData.ppeId}`)
                                    : vehicleLoansMap.get(`mv:${ppeData.mv}`);
                            if (existingLoan) {
                                return {
                                    ...existingLoan,
                                    ...(ppeData.ppeId != null ? {ppeId: ppeData.ppeId} : {}),
                                    ...(existingLoan.sums ? {sums: existingLoan.sums} : {}),
                                };
                            }

                            return {
                                ...(ppeData.ppeId != null ? {ppeId: ppeData.ppeId} : {}),
                            };
                        });

                        if (isMounted) {
                            setVehicleLoansData(initializedLoans);

                            // Initialize validation states based on existing data
                            setLoanValidationStates(initializedLoans.map(loan =>
                                validateLoanData(loan)
                            ));
                        }
                    } else {
                        // No existing data, initialize empty
                        if (isMounted) {
                            setVehicleLoansData(mappedData.map((ppe) => ({
                                ...(ppe.ppeId != null ? {ppeId: ppe.ppeId} : {}),
                            })));
                            setLoanValidationStates(mappedData.map(() => ({
                                hasData: false,
                                missingFields: ['Down payment', 'Terms', 'Total Instalment', 'First repayment date']
                            })));
                        }
                    }
                } catch (vehicleLoanError) {
                    console.error('Failed to fetch vehicle loan data:', vehicleLoanError);
                    // Initialize empty if fetch fails
                    if (isMounted) {
                        setVehicleLoansData(mappedData.map((ppe) => ({
                            ...(ppe.ppeId != null ? {ppeId: ppe.ppeId} : {}),
                        })));
                        setLoanValidationStates(mappedData.map(() => ({
                            hasData: false,
                            missingFields: ['Down payment', 'Terms', 'Total Instalment', 'First repayment date']
                        })));
                    }
                }

            } catch (error) {
                console.error('Failed to fetch hire purchase source data:', error);
                if (isMounted) {
                    showSnackbar('Failed to fetch hire purchase source data', 'error');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchPPEData();

        // Cleanup function: 当组件卸载时标记为已卸载
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, derivedAnnualId, payload?.companyId]);

    // Update loan data function
    const updateLoanData = useCallback((index: number, data: Partial<VehicleLoanRow>) => {
        setVehicleLoansData(prev => {
            const updated = [...prev];
            const existingData = updated[index] || {};
            // 合并数据，确保 sums 对象也被正确合并（保留 prevFinalBalance, currFinalBalance, editedInstalments）
            let mergedSums = data.sums ? {
                ...(existingData.sums || {}),
                ...data.sums,
            } : existingData.sums;
            if (
                existingData.sums
                && vehicleLoanSumsHasTrackableValues(existingData.sums)
                && mergedSums
                && !vehicleLoanSumsHasTrackableValues(mergedSums)
            ) {
                mergedSums = existingData.sums;
            }
            updated[index] = {
                ...existingData,
                ...data,
                ...(mergedSums ? {sums: mergedSums} : {}),
            };

            // If controlled mode, update parent with the updated data
            if (isControlled && payload?.setParDetail) {
                const updatedData: VehicleLoanWorkingPaper = {
                    vehicleLoans: updated.filter(loan => Object.keys(loan).length > 0) as VehicleLoanRow[]
                };
                payload.setParDetail(updatedData);
            }

            return updated;
        });

        // Validate the updated data
        const validationState = validateLoanData(data);
        setLoanValidationStates(prev => {
            const updated = [...prev];
            updated[index] = validationState;
            return updated;
        });
    }, [isControlled, payload, validateLoanData]);

    // Handle opening RepaymentScheduleDrawer
    const handleOpenSchedule = useCallback((index: number, loanData: Partial<VehicleLoanRow>) => {
        // Validate required fields
        if (!loanData.hpAmt || !loanData.terms || !loanData.firstRepayment || !loanData.totalInstalment) {
            showSnackbar('Please fill in all required loan fields first', 'warning');
            return;
        }

        // Build schedule request
        // Convert firstRepayment from dd/MM/yyyy to yyyy-MM-dd for RepaymentScheduleDrawer
        const scheduleRequest: LoanScheduleRequest = {
            loanAmount: loanData.hpAmt,
            tenorYears: loanData.terms / 12, // Convert months to years
            totalInstalment: loanData.totalInstalment,
            firstRepaymentDate: convertToISOFormat(loanData.firstRepayment), // Convert to yyyy-MM-dd
        };

        setCurrentScheduleRequest(scheduleRequest);
        setCurrentLoanIndex(index);
        setScheduleDrawerOpen(true);
    }, [showSnackbar]);

    // Handle RepaymentScheduleDrawer data loaded
    const handleScheduleDataLoaded = useCallback((data: RepaymentScheduleDrawerDataLoaded) => {
        if (currentLoanIndex >= 0 && currentLoanIndex < vehicleLoansData.length) {
            // Update the loan data with calculated sums (VehicleLoanSums type)
            const updatedLoan = {
                ...vehicleLoansData[currentLoanIndex],
                sums: data.sums as any // Type assertion since we know it's VehicleLoanSums for vehicle loans
            };
            updateLoanData(currentLoanIndex, updatedLoan);
        }
    }, [currentLoanIndex, vehicleLoansData, updateLoanData]);

    const loansForTotals = useMemo(
        () => mergeVehicleLoansForTotals(
            vehicleLoansData,
            ppeDataList,
            currentData?.vehicleLoans as VehicleLoanRow[] | undefined,
        ),
        [vehicleLoansData, ppeDataList, currentData?.vehicleLoans],
    );

    // Calculate totals from all vehicle loans (includes last year for save/checks)
    const totals = useMemo(
        () => aggregateVehicleLoanTotals(loansForTotals, deletedIndices),
        [loansForTotals, deletedIndices],
    );

    /** Amount columns; boldLeft separates Interest / Purchase groups. */
    const amountHeaders = useMemo(() => ([
        {key: 'current', name: 'Current', boldLeft: false},
        {key: 'nonCurrent', name: 'Non-current', boldLeft: false},
        {key: 'interest', name: 'Interest', boldLeft: true},
        {key: 'purchase', name: 'Purchase', boldLeft: true},
        {key: 'downPayment', name: 'Down payment', boldLeft: false},
        {key: 'hpAmt', name: 'HP amt', boldLeft: false},
        {key: 'term', name: 'Term', boldLeft: false},
        {key: 'monthlyInstalment', name: 'Monthly instalment', boldLeft: false},
    ] as const), []);

    /** fromPaper code for field-level WP→WP adjustment icons */
    const fieldAdjustFromCodeByKey: Record<string, string> = {
        current: 'BS-CL-OFL',
        nonCurrent: 'BS-NCL-OFL',
        interest: 'PL-FC-LI',
    };

    const fieldAdjListKeyByAmountKey: Record<string, 'currAdj' | 'nonCurrAdj' | 'inAdj'> = {
        current: 'currAdj',
        nonCurrent: 'nonCurrAdj',
        interest: 'inAdj',
    };

    const boldSeparatorSx = {
        // inset shadow survives border-collapse (Total row borderTop would swallow borderLeft)
        boxShadow: (theme: any) => `inset 2px 0 0 0 ${theme.palette.text.primary}`,
    };

    /** Base amount without loan-field adjustments (for LoanFieldAdjustButton perClient). */
    const getLoanAmountBase = useCallback((index: number, amountKey: typeof amountHeaders[number]['key']) => {
        const loan = loansForTotals[index] || {};
        const ppe = ppeDataList[index];
        const sums = loan.sums;
        switch (amountKey) {
            case 'current':
                return resolveVehicleLoanDisplayValue(sums, 'currentYoa', 'currentYoaChange');
            case 'nonCurrent':
                return resolveVehicleLoanDisplayValue(sums, 'nonCurrentYoa', 'nonCurrentYoaChange');
            case 'interest':
                return toNumber(sums?.interestYoa);
            case 'purchase':
                return toNumber(loan.purchaseCost ?? ppe?.purchaseCost);
            case 'downPayment':
                return toNumber(loan.downPayment);
            case 'hpAmt':
                return toNumber(loan.hpAmt);
            case 'term':
                return toNumber(loan.terms);
            case 'monthlyInstalment':
                return toNumber(loan.monthlyInstalment);
            default:
                return 0;
        }
    }, [loansForTotals, ppeDataList]);

    /** Display = base + Σ adjustments on that loan (currAdj / nonCurrAdj / inAdj). */
    const getLoanAmountValue = useCallback((index: number, amountKey: typeof amountHeaders[number]['key']) => {
        const base = getLoanAmountBase(index, amountKey);
        const adjKey = fieldAdjListKeyByAmountKey[amountKey];
        if (!adjKey) return base;
        const loan = loansForTotals[index] as VehicleLoanRow | undefined;
        return base + sumLoanFieldAdjList(loan?.adjustments?.[adjKey]);
    }, [getLoanAmountBase, loansForTotals]);

    const getAmountColumnTotal = useCallback((amountKey: typeof amountHeaders[number]['key']) => {
        if (amountKey === 'term') return null;
        return ppeDataList.reduce((acc, _ppe, index) => {
            if (deletedIndices.has(index)) return acc;
            return acc + getLoanAmountValue(index, amountKey);
        }, 0);
    }, [ppeDataList, deletedIndices, getLoanAmountValue]);

    // Calculate final total for balance check
    const finalTotal = useMemo(() => {
        return totals.balanceYoa;
    }, [totals]);

    // Calculate if unbalanced
    const isUnbalanced = useMemo(() => {
        const targetFinalAmount = (isControlled && payload?.line)
            ? (payload.line.finalAmount || 0)
            : (categoryLine?.finalAmount || 0);
        return calculateIsUnbalanced(targetFinalAmount, finalTotal || 0);
    }, [isControlled, payload?.line, categoryLine?.finalAmount, finalTotal]);

    /** Same shape as saveDraft details — used for WP→WP fromPaper details */
    const buildVehicleLoanSaveDetails = useCallback(() => {
        const filteredLoans = vehicleLoansData
            .filter((loan, index) => !deletedIndices.has(index) && Object.keys(loan).length > 0) as VehicleLoanRow[];

        const vehicleLoansWithAnnualId = filteredLoans.map((loan) => {
            const finalSums = buildVehicleLoanSumsForSave(loan.sums);
            const {adjustments: _adj, ...loanRest} = loan as VehicleLoanRow & {adjustments?: unknown};
            return {
                ...loanRest,
                sums: finalSums,
                annualId: derivedAnnualId,
                code: 'BS-CL-OFL',
                firstRepayment: convertToDisplayFormat(loan.firstRepayment),
                lastRepayment: loan.lastRepayment ? convertToDisplayFormat(loan.lastRepayment) : '',
            };
        });

        const seenLoanIds = new Set<number>();
        const sanitizedVehicleLoans = vehicleLoansWithAnnualId.map((loan: any) => {
            const loanId = loan?.id;
            if (typeof loanId === 'number') {
                if (seenLoanIds.has(loanId)) {
                    const {id: _ignoredId, ...rest} = loan;
                    const sums = rest?.sums ? {...rest.sums} : undefined;
                    if (sums && 'id' in sums) {
                        delete (sums as any).id;
                    }
                    return {
                        ...rest,
                        ...(sums ? {sums} : {}),
                    };
                }
                seenLoanIds.add(loanId);
            }
            return loan;
        });

        return {vehicleLoans: sanitizedVehicleLoans};
    }, [vehicleLoansData, deletedIndices, derivedAnnualId]);

    const persist = async (opts?: WpPersistOptions): Promise<boolean> => {
        const {closeOnSuccess, silent} = resolveWpPersistOptions(opts);
        if (saving) return false;

        if (!isEditableMode) {
            if (!silent) showSnackbar('当前为只读模式，无法保存', 'warning');
            return false;
        }

        if (!validateIssueMarkBeforeSave()) {
            if (!silent) {
                showSnackbar('Please provide issue description when marked as issue', 'error');
            }
            return false;
        }

        // Validate vehicle loans data before saving
        // const { valid, errors, filteredLoans } = validateVehicleLoans(vehicleLoansData);

        // if (!valid) {
        //     setValidationErrors(errors);
        //     showSnackbar('Please fix validation errors before saving', 'error');
        //     return;
        // }

        // Clear validation errors if valid
        // setValidationErrors({});
        // 排除已删除的记录和空记录，后端通过 ID 比对自动将缺失的记录标记为 inValid=1
        const filteredLoans = vehicleLoansData
            .filter((loan, index) => !deletedIndices.has(index) && Object.keys(loan).length > 0) as VehicleLoanRow[];

        setSaving(true);

        try {
            const validVehicleLoans = filteredLoans;

            // Add annualId and code to each vehicle loan
            // Use manual update values (xxxChange) over calculated values when assembling data
            const vehicleLoansWithAnnualId = validVehicleLoans.map(loan => {
                const finalSums = buildVehicleLoanSumsForSave(loan.sums);
                const {adjustments: _adj, ...loanRest} = loan as VehicleLoanRow & {adjustments?: unknown};

                return {
                    ...loanRest,
                    sums: finalSums,
                    annualId: derivedAnnualId,
                    code: 'BS-CL-OFL',
                    firstRepayment: convertToDisplayFormat(loan.firstRepayment), // Convert to dd/MM/yyyy
                    lastRepayment: loan.lastRepayment ? convertToDisplayFormat(loan.lastRepayment) : '' // Convert to dd/MM/yyyy
                };
            });

            // Safeguard: If duplicate ids exist (e.g. MV used as key), strip duplicate ids to avoid overwriting.
            const seenLoanIds = new Set<number>();
            const sanitizedVehicleLoans = vehicleLoansWithAnnualId.map((loan: any) => {
                const loanId = loan?.id;
                if (typeof loanId === 'number') {
                    if (seenLoanIds.has(loanId)) {
                        const {id: _ignoredId, ...rest} = loan;
                        const sums = rest?.sums ? {...rest.sums} : undefined;
                        if (sums && 'id' in sums) {
                            delete (sums as any).id;
                        }
                        return {
                            ...rest,
                            ...(sums ? {sums} : {}),
                        };
                    }
                    seenLoanIds.add(loanId);
                }
                return loan;
            });

            // Build details payload for vehicle loans
            const detailsPayload = {
                vehicleLoans: sanitizedVehicleLoans,
            };

            await saveOrUpdate({
                companyId: Number(payload?.companyId),
                annualId: derivedAnnualId,
                code: backendCode,
                details: detailsPayload,
                issueMark: buildIssueMarkPayload(issueMarkState, payload?.line?.wpStatus, Boolean((payload as any)?.line?.outstanding)),
            });

            if (!silent) {
                notifySaveResult('Vehicle loan adjustments saved successfully', 'success');
            }

            await invalidateWpSpecialSyncQueries(
                queryClient,
                Number(payload?.companyId),
                derivedAnnualId,
                backendCode,
                'VehicleLoan',
            );
            // list 无感：saveOrUpdate 已 notify — 勿再 setDataSaved

            if (closeOnSuccess) {
                setTimeout(() => {
                    onClose();
                }, 100);
            }
            return true;
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to save vehicle loan adjustments';
            if (!silent) showSnackbar(`${errorMessage}`, 'error');
            console.error('VehicleLoan saveDraft error:', error);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        await persist({closeOnSuccess: false, silent: false});
    };

    const [dialogContentEl, setDialogContentEl] = useState<HTMLElement | null>(null);
    const {
        status: autoSaveStatus,
        lastSavedAt,
    } = useWpIdleAutoSave({
        open,
        enabled: isEditableMode && !showFieldAdjustmentButton && initialized,
        persist,
        contentEl: dialogContentEl,
    });

    /**
     * Unified content width = max(toolbar single-row intrinsic, table header intrinsic).
     * Parent uses fit-content + column flex; both children use minWidth:max-content + width:100%
     * so the wider of the two drives dialog width, and the narrower stretches to match.
     * Headers never shrink below their natural size.
     */
    const unifiedContentSx = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: 'fit-content',
        maxWidth: '95vw',
        height: '100%',
    } as const;

    const intrinsicStretchSx = {
        width: '100%',
        minWidth: 'max-content',
        flexShrink: 0,
    } as const;

    const vehicleLoanContent = (
        <Box sx={intrinsicStretchSx}>
            {loading ? (
                <Box sx={{width: 'fit-content', maxWidth: '100%', overflowX: 'auto'}}>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            minWidth: 900,
                            p: 1,
                        }}
                    >
                        <Skeleton variant="rectangular" height={36} sx={{mb: 1}}/>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={28} sx={{mb: 0.5}}/>
                        ))}
                        <Skeleton variant="rectangular" height={32} sx={{mt: 1}}/>
                    </Box>
                </Box>
            ) : (
                <Box sx={{width: '100%', minWidth: 'max-content'}}>
                    <Box sx={{mb: 1}}>
                        <WpNotesAnchor/>
                    </Box>
                <TableContainer sx={{width: '100%', minWidth: 'max-content'}}>
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            width: '100%',
                            minWidth: 'max-content',
                            tableLayout: 'auto',
                            '& td, & th': {whiteSpace: 'nowrap'},
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <Typography variant="subtitle2" noWrap>MV</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="subtitle2" noWrap>Agreement no</Typography>
                                </TableCell>
                                {amountHeaders.map((h) => (
                                    <TableCell
                                        key={h.key}
                                        align="center"
                                        sx={h.boldLeft ? boldSeparatorSx : undefined}
                                    >
                                        <Typography variant="subtitle2" noWrap>{h.name}</Typography>
                                    </TableCell>
                                ))}
                                <TableCell align="center"/>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{'& .MuiTableCell-root': {paddingTop: '2px', paddingBottom: '2px'}}}>
                            {ppeDataList.map((ppeData, index) => {
                                if (deletedIndices.has(index)) return null;
                                const loanData = loansForTotals[index] || {};
                                return (
                                    <TableRow key={index} hover>
                                        <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                            <Typography variant="body2" noWrap>
                                                {loanData.mv || ppeData.mv || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                            <Typography variant="body2" noWrap>
                                                {loanData.agreementNo || '-'}
                                            </Typography>
                                        </TableCell>
                                        {amountHeaders.map((h) => (
                                            <TableCell
                                                key={h.key}
                                                align="center"
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    ...(h.boldLeft ? boldSeparatorSx : {}),
                                                }}
                                            >
                                                {h.key === 'term' ? (
                                                    <Typography variant="body2" noWrap>
                                                        {getLoanAmountValue(index, h.key) || '-'}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                        justifyContent: 'center',
                                                    }}>
                                                        {(() => {
                                                            const adjKey = fieldAdjListKeyByAmountKey[h.key];
                                                            const fieldAdj = adjKey
                                                                ? getLoanRowFieldAdj((loanData as VehicleLoanRow).adjustments, adjKey)
                                                                : undefined;
                                                            const itemLabel = [
                                                                String(loanData.mv || ppeData.mv || '').trim(),
                                                                h.name,
                                                            ].filter(Boolean).join(' — ') || h.name;
                                                            return (
                                                                <LoanAdjAmountBadge
                                                                    value={getLoanAmountValue(index, h.key)}
                                                                    currencySymbol={payload?.currencySymbol}
                                                                    adjustments={fieldAdj}
                                                                    itemLabel={itemLabel}
                                                                    onOpenDetails={adjDrawer.handleOpen}
                                                                />
                                                            );
                                                        })()}
                                                        {!isControlled && fieldAdjustFromCodeByKey[h.key] && (
                                                            <LoanFieldAdjustButton
                                                                fromCode={fieldAdjustFromCodeByKey[h.key]}
                                                                itemName={String(loanData.mv || ppeData.mv || '').trim() || h.name}
                                                                fieldLabel={h.name}
                                                                perClient={toNumber(fieldAdjustPerClientByKey[h.key])}
                                                                existingAdjustments={getLoanRowFieldAdj(
                                                                    (loanData as VehicleLoanRow).adjustments,
                                                                    fieldAdjListKeyByAmountKey[h.key],
                                                                )}
                                                                statement={h.key === 'interest' ? 'PL' : 'BS'}
                                                                onActivate={handleFieldAdjustActivate}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                justifyContent: 'center',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {!isEditableMode && loanData && Object.keys(loanData).length > 0 && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenSchedule(index, loanData)}
                                                        title="View Repayment Schedule"
                                                        sx={{color: 'primary.main'}}
                                                    >
                                                        <CalendarMonthIcon fontSize="small"/>
                                                    </IconButton>
                                                )}
                                                <VehicleLoanDetail
                                                    yoa={yoa || ""}
                                                    lastYoa={lastYoa || ""}
                                                    currencySymbol={currency || payload?.currencySymbol || 'HKD'}
                                                    triggerVariant="icon"
                                                    annualId={payload?.annualId}
                                                    initialData={loanData}
                                                    onDataChange={(data) => updateLoanData(index, data)}
                                                    parentMV={ppeData.mv}
                                                    parentPurchaseCost={ppeData.purchaseCost}
                                                    readOnly={!isEditableMode}
                                                    hideLastYoa={firstAudit === 1}
                                                    periodTo={periodTo}
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {/* Total row at bottom */}
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        borderTop: '2px solid',
                                        borderTopColor: 'divider',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{fontWeight: 'bold'}} noWrap>Total</Typography>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        borderTop: '2px solid',
                                        borderTopColor: 'divider',
                                    }}
                                />
                                {amountHeaders.map((h) => {
                                    const total = getAmountColumnTotal(h.key);
                                    return (
                                        <TableCell
                                            key={h.key}
                                            align="center"
                                            sx={{
                                                verticalAlign: 'middle',
                                                fontWeight: 'bold',
                                                borderTop: '2px solid',
                                                borderTopColor: 'divider',
                                                ...(h.boldLeft ? boldSeparatorSx : {}),
                                            }}
                                        >
                                            {total == null ? null : (
                                                <MoneyField
                                                    value={total}
                                                    currencySymbol={payload?.currencySymbol}
                                                    showCents={true}
                                                    align="center"
                                                    compact={true}
                                                />
                                            )}
                                        </TableCell>
                                    );
                                })}
                                <TableCell
                                    align="center"
                                    sx={{
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        borderTop: '2px solid',
                                        borderTopColor: 'divider',
                                    }}
                                />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
            )}
            <LoanFieldAdjustPanel
                target={fieldAdjustTarget}
                onClose={handleFieldAdjustClose}
                companyId={payload?.companyId ? Number(payload.companyId) : undefined}
                annualId={Number(payload?.annualId) || derivedAnnualId}
                currency={currency || payload?.currencySymbol}
                onParentClose={onClose}
                onActionStateChange={handleFieldAdjustActionStateChange}
                buildLoanSaveDetails={buildVehicleLoanSaveDetails}
            />
            <EpWorkingContent
                    companyId={Number(payload?.companyId)}
                    annualId={Number(payload?.annualId) || derivedAnnualId}
                    fsId={Number(payload?.line?.fsId) || undefined}
                    initialNotes={(payload?.line as any)?.notes}
                    smeFrsData={isMultiPeriodTabs ? epWorkingMeta.smeFrs : currentData?.['SME-FRS']}
                    wokeDoneData={isMultiPeriodTabs ? epWorkingMeta.wokeDone : currentData?.['WOKE_DONE']}/>
        </Box>
    );

    if (embed) return vehicleLoanContent;

    return (
        <>
            <Dialog
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
                }}
                maxWidth={false}
                sx={{
                    '& .MuiDialog-paper': {
                        width: 'fit-content',
                        maxWidth: '95vw',
                    },
                    '& .MuiDialogContent-root': {
                        overflowX: 'auto',
                    },
                }}
            >
                <DialogTitle>{companyName || payload?.itemName || 'Vehicle Loan'} Working Paper</DialogTitle>
                <DialogContent ref={dialogContentElRef(setDialogContentEl)} dividers>
                    {(loading || !initialized) ? (
                        <LoanWpSkeleton variant="vehicle"/>
                    ) : (
                        <Box sx={unifiedContentSx}>
                            {/* Sticky header: Mark as issue + checks in one row */}
                            <Box sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 100,
                                backgroundColor: 'background.paper',
                                ...intrinsicStretchSx,
                            }}>
                                <WpHeader
                                    itemName={scheduleName}
                                    workingName={'Vehicle Loan Working Paper'}
                                    periodFrom={headerPeriodFrom}
                                    periodTo={headerPeriodTo}
                                    preparedBy={payload?.preparedBy}
                                    reviewedBy={payload?.reviewedBy}
                                    dateText={today}
                                    hidePeriodTabs={isMultiPeriodTabs}
                                />

                                {payload?.workflow === 'account' && (
                                    <Box sx={{display: 'flex', justifyContent: 'flex-start', mb: 1}}>
                                        <WpGlInvoiceDetailButton onClick={() => glDetail.setOpen(true)}
                                                                 disabled={glDetail.rows.length === 0}/>
                                    </Box>
                                )}

                                <Divider sx={{mb: 2}}/>

                                <Box
                                    sx={{
                                        ...intrinsicStretchSx,
                                        '& > .MuiBox-root': {flexWrap: 'nowrap'},
                                    }}
                                >
                                    <WpCheckToolbar
                                        code={backendCode}
                                        companyId={Number(payload?.companyId)}
                                        annualId={derivedAnnualId}
                                        initialWpStatus={currentData?.wpStatus ?? payload?.line?.wpStatus}
                                        initialWpIssueRemark={currentData?.wpIssueRemark ?? payload?.line?.wpIssueRemark}
                                        initialOutstanding={Boolean(currentData?.outstanding ?? payload?.line?.outstanding)}
                                        initialOutstandingRemark={currentData?.outstandingRemark ?? payload?.line?.outstandingRemark}
                                        onIssueMarkChange={setIssueMarkState}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: 2,
                                            mb: 2,
                                            width: '100%',
                                            minWidth: 'max-content',
                                            flexWrap: 'nowrap',
                                        }}>
                                            <Box sx={{minWidth: 280, maxWidth: 320, flexShrink: 0}}>
                                                <WpPerCheck
                                                    payload={{
                                                        perName: 'Current',
                                                        currentValue: totals.current || 0,
                                                        lastYearValue: 0,
                                                        yoa: stablePerCheckYoa.yoa,
                                                        lastYoa: stablePerCheckYoa.lastYoa,
                                                    }}
                                                    code="BS-CL-OFL"
                                                    currentPer={(ok) => setCurrentPerOk(ok)}
                                                    options={{ignoreLastYear: true}}
                                                />
                                            </Box>

                                            <Box sx={{minWidth: 280, maxWidth: 320, flexShrink: 0}}>
                                                <WpPerCheck
                                                    payload={{
                                                        perName: 'Non-Current',
                                                        currentValue: totals.nonCurrent || 0,
                                                        lastYearValue: 0,
                                                        yoa: stablePerCheckYoa.yoa,
                                                        lastYoa: stablePerCheckYoa.lastYoa,
                                                    }}
                                                    code="BS-NCL-OFL"
                                                    currentPer={(ok) => setNonCurrentPerOk(ok)}
                                                    options={{ignoreLastYear: true}}
                                                />
                                            </Box>

                                            <Box sx={{minWidth: 280, maxWidth: 360, flexShrink: 0}}>
                                                <WpInterestPerCheck
                                                    payload={{
                                                        yoa: stablePerCheckYoa.yoa,
                                                        lastYoa: stablePerCheckYoa.lastYoa,
                                                    }}
                                                    sourceOverride={{
                                                        source: 'vehicle',
                                                        currentValue: totals.interestYoa || 0,
                                                        lastYearValue: totals.interestLastYoa || 0,
                                                    }}
                                                    annualId={derivedAnnualId}
                                                    companyId={Number(payload?.companyId || 0)}
                                                    currentPer={(ok) => setInterestPerOk(ok)}
                                                />
                                            </Box>
                                        </Box>
                                    </WpCheckToolbar>
                                </Box>
                                <Divider sx={{mb: 1}}/>
                                <WpSeamlessPeriodTabsSlot enabled={isMultiPeriodTabs}/>
                            </Box>
                            {vehicleLoanContent}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <AutoSaveStatus status={autoSaveStatus} lastSavedAt={lastSavedAt}/>
                    {isEditableMode && showFieldAdjustmentButton && (
                        <Button
                            variant="contained"
                            onClick={() => fieldAdjustAction?.submitAdjustment()}
                            disabled={saving || loading || !initialized || !!fieldAdjustAction?.isProcessing}
                            sx={{mr: 1}}
                            endIcon={fieldAdjustAction?.isProcessing ? <CircularProgress size={16}/> : null}
                        >
                            {fieldAdjustAction?.isProcessing ? 'Processing...' : 'Adjustment'}
                        </Button>
                    )}
                    {isEditableMode && !showFieldAdjustmentButton && (
                        <Button
                            variant="contained"
                            onClick={saveDraft}
                            disabled={saving || loading || !initialized}
                            sx={{mr: 1}}
                            startIcon={saving ? <CircularProgress size={16}/> : null}
                        >{saving ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        disabled={saving}
                    >{isEditableMode ? 'Cancel' : 'Close'}</Button>
                </DialogActions>
                <EmptyConfirmDialog
                    open={emptyConfirmOpen}
                    message="No any hire purchase of taxi cabs or motor vehicle. Would you like to fill in the Hire purchase section first?"
                    confirmActions={[
                        {
                            label: 'Motor Vehicle',
                            onClick: () => {
                                setEmptyConfirmOpen(false);
                                onClose();
                                payload?.onRequestOpenCode?.('BS-NCA-PPE');
                            },
                        },
                        {
                            label: 'Taxi Cab',
                            onClick: () => {
                                setEmptyConfirmOpen(false);
                                onClose();
                                payload?.onRequestOpenCode?.('BS-NCA-ITL');
                            },
                        },
                    ]}
                    onCancel={() => {
                        setEmptyConfirmOpen(false);
                        onClose();
                    }}
                />
                <SnackbarField {...snackbarFieldProps} />
            </Dialog>

            {payload?.workflow === 'account' && (
                <WpGlInvoiceDetailDialog open={glDetail.open} onClose={() => glDetail.setOpen(false)}
                                         code={glCode} total={glDetail.total} rows={glDetail.rows}/>
            )}

            {/* RepaymentScheduleDrawer */}
            <RepaymentScheduleDrawer
                open={scheduleDrawerOpen}
                onClose={() => setScheduleDrawerOpen(false)}
                loanType="vehicle"
                annualId={derivedAnnualId}
                scheduleRequest={currentScheduleRequest}
                onDataLoaded={handleScheduleDataLoaded}
            />
            <AdjustmentDetailsDrawer
                open={adjDrawer.open}
                onClose={adjDrawer.handleClose}
                itemName={adjDrawer.itemName}
                currency={currency || payload?.currencySymbol || 'HKD'}
                headerData={adjDrawer.headerData}
                headerDataLoading={adjDrawer.loading}
            />
        </>
    );
}
